import { ThumbUpIcon, ThumbDownIcon, ThumbUpOutlinedIcon, ThumbDownOutlinedIcon } from '@/components/icons';
import { Box, IconButton, Stack, useTheme } from '@mui/material';
import type { ChatMessage } from './ChatBot';
import { CopyMessageButton } from './CopyMessageButton';
import { trackWidgetEvent } from '../utils/analytics';
import { getTimezone } from '../utils/visitor-context';
import { scaledFontSize } from './createWidgetTheme';
import { useWidgetAnnouncer } from './WidgetAnnouncerProvider';
import { FeedbackCommentCard } from './_parts/FeedbackCommentCard';
import { useMessageFeedbackDisclosure } from './useMessageFeedbackDisclosure';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';

interface MessageFeedbackProps {
  timestamp: number;
  feedback?: ChatMessage['feedback'];
  threadId: string;
  botMessageText: string;
  messageId?: string;
  isStreaming?: boolean;
  onFeedback: (rating: 'up' | 'down' | null, comment?: string) => void;
}

export function MessageFeedback({
  timestamp,
  feedback,
  threadId,
  botMessageText,
  messageId,
  isStreaming = false,
  onFeedback,
}: MessageFeedbackProps) {
  const theme = useTheme();
  const { t, locale } = useWidgetI18n();
  const { announce } = useWidgetAnnouncer();
  const rating = feedback?.rating ?? null;
  const {
    comment,
    setComment,
    submitted,
    commentBoxOpen,
    isCommentFormOpen,
    commentId,
    commentRef,
    notHelpfulRef,
    markSubmitted,
  } = useMessageFeedbackDisclosure({ rating, timestamp, announce });

  const basePayload = {
    threadId,
    messageTimestamp: timestamp,
    timezone: getTimezone() ?? 'unknown',
    ...(messageId && { messageId }),
  };

  const handleThumbClick = (thumb: 'up' | 'down') => {
    if (rating === thumb) {
      trackWidgetEvent('Chat Widget - User Feedback Cleared', basePayload);
      onFeedback(null);
      setComment('');
      return;
    }
    const eventName =
      thumb === 'up'
        ? 'Chat Widget - User Feedback Thumbs Up'
        : 'Chat Widget - User Feedback Thumbs Down';
    trackWidgetEvent(eventName, basePayload);
    onFeedback(thumb);
    if (thumb === 'up') setComment('');
  };

  // Mixpanel: thumbs-down comment submission. Do not remove or slim down this
  // payload without explicit product approval. `reason` is the user's submitted
  // feedback text and is intentionally sent on this event.
  const handleCommentSubmit = () => {
    const trimmed = comment.trim();
    if (!trimmed) return;
    trackWidgetEvent('Chat Widget - User Feedback Comment Submitted', {
      ...basePayload,
      reason: trimmed,
      commentLength: trimmed.length,
    });
    onFeedback('down', trimmed);
    markSubmitted();
  };

  return (
    <Box sx={{ mt: 0.5 }}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <CopyMessageButton text={botMessageText} disabled={isStreaming} />
        <IconButton
          size="small"
          lang={locale}
          aria-label={t('feedback.helpful')}
          aria-pressed={rating === 'up'}
          onClick={() => handleThumbClick('up')}
          sx={{ color: 'text.disabled', p: 0.5 }}
        >
          {rating === 'up' ? (
            <ThumbUpIcon sx={{ fontSize: scaledFontSize(theme, 16) }} aria-hidden />
          ) : (
            <ThumbUpOutlinedIcon sx={{ fontSize: scaledFontSize(theme, 16) }} aria-hidden />
          )}
        </IconButton>
        <IconButton
          ref={notHelpfulRef}
          size="small"
          lang={locale}
          aria-label={t('feedback.notHelpful')}
          aria-pressed={rating === 'down'}
          aria-expanded={isCommentFormOpen}
          aria-controls={isCommentFormOpen ? commentId : undefined}
          onClick={() => handleThumbClick('down')}
          sx={{ color: 'text.disabled', p: 0.5 }}
        >
          {rating === 'down' ? (
            <ThumbDownIcon sx={{ fontSize: scaledFontSize(theme, 16) }} aria-hidden />
          ) : (
            <ThumbDownOutlinedIcon sx={{ fontSize: scaledFontSize(theme, 16) }} aria-hidden />
          )}
        </IconButton>
      </Stack>

      {commentBoxOpen && (
        <FeedbackCommentCard
          commentId={commentId}
          comment={comment}
          submitted={submitted}
          commentRef={commentRef}
          onCommentChange={setComment}
          onSubmit={handleCommentSubmit}
        />
      )}
    </Box>
  );
}

export default MessageFeedback;
